import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../../shared/legacyData/legacyCallable";
import AddETQA from './AddETQA';
import './ETQADashboard.css';

const ETQADashboard = () => {
  // Data + UI state
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmail, setEditingEmail] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [editProfilePicDataUrl, setEditProfilePicDataUrl] = useState('');
  const [editProfilePicName, setEditProfilePicName] = useState('');
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    surname: '',
    number: '',
    email: '',
    profilePic: '',
  });
  // External dependencies
  const company = useSelector((s) => s.heroUser?.company) || 'TPC_Group';
  const collectionBrand = useSelector((s) => s.heroUser?.collectionBrand) || 'TechPerformanceCentre';
  const functions = getFunctions();
  const getSystemDocuments = httpsCallable(functions, 'getSystemDocuments');
  const firestoreOps = httpsCallable(functions, 'firestoreOperations');

  // Fetch ETQA officers
  useEffect(() => {
    const fetchOfficers = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getSystemDocuments({
          company,
          collectionBrand,
          systemName: 'etqaOfficer',
        });
        const docs = res.data?.data;
        setOfficers(Array.isArray(docs) ? docs : []);
      } catch (err) {
        setError('Failed to fetch officers');
      }
      setLoading(false);
    };
    fetchOfficers();
    // eslint-disable-next-line
  }, []);

  // Add ETQA officer
  const handleAdd = (data) => {
    setOfficers((prev) => [...prev, data]);
    setShowAddForm(false);
  };

  // Edit ETQA officer
  const handleEditStart = (officer) => {
    setEditingEmail(officer.email);
    setEditProfilePicDataUrl('');
    setEditProfilePicName('');
    setEditForm({
      name: officer.name || '',
      surname: officer.surname || '',
      number: officer.number || '',
      email: officer.email || '',
      profilePic: officer.profilePic || '',
    });
  };

  // Handle edit field changes
  const handleEditChange = ({ target }) => {
    setEditForm((prev) => ({ ...prev, [target.name]: target.value }));
  };

  // Cancel edit mode
  const handleEditCancel = () => {
    setEditingEmail('');
    setEditProfilePicDataUrl('');
    setEditProfilePicName('');
    setEditForm({
      name: '',
      surname: '',
      number: '',
      email: '',
      profilePic: '',
    });
  };

  // Handle edit profile picture upload
  const handleEditFileChange = ({ target }) => {
    const file = target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditProfilePicDataUrl(e.target.result);
      setEditProfilePicName(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Persist edit changes
  const handleEditSave = async () => {
    setError('');
    try {
      let profilePicUrl = editForm.profilePic || '';
      if (editProfilePicDataUrl) {
        const base64 = editProfilePicDataUrl.split(',')[1];
        const ext = editProfilePicName.split('.').pop()?.toLowerCase() || 'jpg';
        const fileName = `profile_${Date.now()}.${ext}`;
        const uploadPic = httpsCallable(functions, 'uploadProfilePicFacilitator');
        const uploadRes = await uploadPic({
          company,
          collectionBrand,
          system: 'ETQA',
          uid: editingEmail,
          fileName,
          fileBuffer: base64,
        });
        profilePicUrl = uploadRes.data?.url || profilePicUrl;
      }

      await firestoreOps({
        operation: 'update',
        company,
        collectionBrand,
        systemName: 'etqaOfficer',
        uniqueId: editingEmail,
        documentData: {
          name: editForm.name,
          surname: editForm.surname,
          number: editForm.number,
          profilePic: profilePicUrl,
        },
      });

      setOfficers((prev) =>
        prev.map((o) =>
          o.email === editingEmail
            ? {
                ...o,
                name: editForm.name,
                surname: editForm.surname,
                number: editForm.number,
                profilePic: profilePicUrl,
              }
            : o
        )
      );
      handleEditCancel();
    } catch (err) {
      setError('Failed to update officer');
    }
  };

  // Open delete confirmation
  const requestDelete = (officer) => {
    setConfirmingDelete(officer);
  };

  // Execute delete
  const handleDelete = async () => {
    if (!confirmingDelete) return;
    setError('');
    try {
      await firestoreOps({
        operation: 'delete',
        company,
        collectionBrand,
        systemName: 'etqaOfficer',
        uniqueId: confirmingDelete.email,
      });
      setOfficers((prev) =>
        prev.filter((o) => o.email !== confirmingDelete.email)
      );
      if (editingEmail === confirmingDelete.email) {
        handleEditCancel();
      }
      setConfirmingDelete(null);
    } catch (err) {
      setError('Failed to delete officer');
    }
  };

  // Dashboard UI
  return (
    <div className="etqa-dashboard">
      <div className="etqa-header">
        <div className="etqa-header-content">
          <h2 className="etqa-title">Education And Training Quality Assurance Officers</h2>
          <span className="etqa-subtitle">
            Manage registered officers
          </span>
        </div>
      </div>

      <div className="etqa-add-row">
        <button
          className="etqa-add-btn"
          type="button"
          onClick={() => setShowAddForm((prev) => !prev)}
        >
          {showAddForm ? 'Close' : 'Add ETQA Officer'}
        </button>
      </div>

      {showAddForm && (
        <div className="etqa-card etqa-add-panel">
          <AddETQA
            company={company}
            collectionBrand={collectionBrand}
            systemName="etqaOfficer"
            onAdd={handleAdd}
            onComplete={() => setShowAddForm(false)}
            existingEmails={officers.map((o) =>
              (o.email || '').trim().toLowerCase()
            )}
          />
        </div>
      )}

      <div className="etqa-card etqa-table-card">
        <div className="etqa-table-header">
          <div className="etqa-table-meta">
            {loading ? 'Loading officers...' : `${officers.length} officers`}
          </div>
        </div>
        {error && <div className="etqa-error">{error}</div>}
        <div className="etqa-table-wrapper">
          <table className="etqa-table">
            <thead>
              <tr>
                <th>Officer</th>
                <th>Email</th>
                <th>Number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {officers.map((o) => (
                <tr key={o.email}>
                  <td>
                    <div className="etqa-officer">
                      <div className="etqa-avatar">
                        {o.profilePic ? (
                          <img src={o.profilePic} alt="Profile" />
                        ) : (
                          <span>{(o.name || 'U')[0]}</span>
                        )}
                      </div>
                      <div>
                        <div className="etqa-name">
                          {editingEmail === o.email ? (
                            <div className="etqa-edit-row">
                              <input
                                className="etqa-edit-input"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditChange}
                                placeholder="Name"
                              />
                              <input
                                className="etqa-edit-input"
                                name="surname"
                                value={editForm.surname}
                                onChange={handleEditChange}
                                placeholder="Surname"
                              />
                            </div>
                          ) : (
                            <>
                              {o.name} {o.surname}
                            </>
                          )}
                        </div>
                        <div className="etqa-role">ETQA Officer</div>
                      </div>
                    </div>
                  </td>
                  <td>{o.email}</td>
                  <td>
                    {editingEmail === o.email ? (
                      <input
                        className="etqa-edit-input"
                        name="number"
                        value={editForm.number}
                        onChange={handleEditChange}
                        placeholder="Number"
                      />
                    ) : (
                      o.number
                    )}
                  </td>
                  <td>
                    {editingEmail === o.email ? (
                      <div className="etqa-actions">
                        <input
                          className="etqa-edit-input"
                          name="profilePicFile"
                          type="file"
                          accept="image/*"
                          onChange={handleEditFileChange}
                        />
                        <button
                          className="etqa-action-btn etqa-action-save"
                          type="button"
                          onClick={handleEditSave}
                        >
                          Save
                        </button>
                        <button
                          className="etqa-action-btn etqa-action-cancel"
                          type="button"
                          onClick={handleEditCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="etqa-actions">
                        <button
                          className="etqa-action-btn"
                          type="button"
                          onClick={() => handleEditStart(o)}
                        >
                          Edit
                        </button>
                        <button
                          className="etqa-action-btn etqa-action-delete"
                          type="button"
                          onClick={() => requestDelete(o)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && officers.length === 0 && (
                <tr>
                  <td colSpan={4} className="etqa-empty">
                    No officers found yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmingDelete && (
        <div className="etqa-modal-backdrop">
          <div className="etqa-modal">
            <h3>Confirm removal</h3>
            <p>
              Remove {confirmingDelete.name || ''} {confirmingDelete.surname || ''}
              {' '}({confirmingDelete.email})?
            </p>
            <div className="etqa-modal-actions">
              <button
                className="etqa-action-btn etqa-action-delete"
                type="button"
                onClick={handleDelete}
              >
                Remove
              </button>
              <button
                className="etqa-action-btn etqa-action-cancel"
                type="button"
                onClick={() => setConfirmingDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ETQADashboard;
